﻿import { EditorProps, EntityDialog, IGetEditValue, IReadOnly, ISetEditValue, IStringValue, Lookup, PropertyDialog, tryGetWidget, Widget } from "@serenity-is/corelib";
import { SqlProcLookupParameter } from "@/ServerTypes/Data/SqlProcLookupParameter";
import { FormatLookupEditorBase, FormatLookupEditorOptions } from "@/Data/List/FormatLookupEditor";
import { microFormat } from "@/Common/Helpers/MicroFormat";
import { SqlProcLookupQueryType } from "@/ServerTypes/Data/SqlProcLookupQueryType";
import { SqlProcLookupService } from "@/ServerTypes/Data/SqlProcLookupService";
import { SqlProcLookupListRequest } from "@/ServerTypes/Data/SqlProcLookupListRequest";
import { nsData } from "@/ServerTypes/Namespaces";

export interface SqlProcLookupEditorOptions extends FormatLookupEditorOptions {
    objectName?: string;
    parameters?: SqlProcLookupParameter[];
    expiration?: number;
    permission?: string;
    multiple?: boolean;
    delimited?: boolean;
    idField: string;
    textField: string;
    resultFormat?: string;
    selectionFormat?: string;
    textFormat?: string;
    queryType?: SqlProcLookupQueryType;
}

export class SqlProcLookupEditor extends FormatLookupEditorBase<SqlProcLookupEditorOptions, any>
    implements IReadOnly, IGetEditValue, ISetEditValue, IStringValue {
    static override[Symbol.typeInfo] = this.registerEditor(nsData, [IReadOnly, IGetEditValue, ISetEditValue, IStringValue]);

    private ownerWidget: EntityDialog<any, any> | PropertyDialog<any, any> | null = null;
    private loaded = false;

    constructor(options: EditorProps<SqlProcLookupEditorOptions>) {
        super(options);

        this.findOwnerDialog();
        this.bindAfterLoadEvents();
    }

    private bindAfterLoadEvents() {
        const oldAfterLoadEntity = this.ownerWidget["afterLoadEntity"];

        let afterLoad = false, onOpen = false;
        // re-select the current values after the entity is loaded, and the dialog is opened to enrich the initial values
        // with the current values from the server (such as their name).
        const reSelect = (opt: { load?: boolean; open?: any }) => {
            afterLoad = afterLoad || !!opt.load;
            onOpen = onOpen || !!opt.open;

            if (afterLoad && onOpen) {
                const oldValues = this.values.slice();

                if (this.isMultiple())
                    this.set_values(null);
                else
                    this.value = '';
                this.set_values(oldValues);
            }
        }

        this.ownerWidget["afterLoadEntity"] = () => {
            const ret = oldAfterLoadEntity?.call(this.ownerWidget);
            this.updateLookup().then(() => {
                this.loaded = true;
                reSelect({load: true});
            })

            return ret;
        };

        this.ownerWidget.onOpen(() => {
            reSelect({open: true});
        }, {oneOff: true});
    }

    private findOwnerDialog() {
        if (this.domNode?.id == null)
            return;

        let owner = this.domNode.parentElement;
        while (owner != null) {
            const ownerWidget = tryGetWidget(owner, Widget);
            if (ownerWidget != null && ownerWidget instanceof EntityDialog || ownerWidget instanceof PropertyDialog) {
                this.ownerWidget = ownerWidget;
                break;
            }

            owner = owner.parentElement;
        }

        if (this.ownerWidget == null)
            throw new Error("SqlProcLookupEditor can only be used inside EntityDialog or PropertyDialog!");
    }

    private makeRequest() {
        const request: SqlProcLookupListRequest = {
            ObjectName: this.options.objectName,
            QueryType: this.options.queryType,
            ParameterDefinitions: this.options.parameters?.map(x => ({
                Name: x.Name,
                Type: x.Type,
                Required: x.Required
            })) ?? [],
            Parameters: {}
        };

        const data = {
            ...this.ownerWidget?.["entity"],
            ...this.ownerWidget?.["getSaveEntity"]?.(),
        }

        for (const p of this.options.parameters ?? []) {
            request.Parameters[p.Name] = microFormat(p.Value ?? '', data);
        }

        return request;
    }

    private async getCurrentLookup() {
        const request = this.makeRequest();

        const response = (await SqlProcLookupService.List(request)).Entities;
        return {
            items: response,
            itemById: response.reduce((acc, x) => ({...acc, [x[this.options.idField]]: x}), {}),
            idField: this.options.idField,
            textField: this.options.textField,
            parentIdField: null
        };
    }

    private async updateLookup() {
        this.lookup = await this.getCurrentLookup();
        return this.lookup;
    }

    public override hasAsyncSource(): boolean {
        return true;
    }

    protected override async getLookupAsync(): Promise<Lookup<any>> {
        if (!this.loaded) {
            return this.lookup;
        }

        return this.updateLookup();
    }
}